import type { ErrorEvent } from '@sentry/browser';

export default class SentryErrorFilter {
    readonly #noisyMessages: RegExp[] = [
        /Java object is gone/i,
        /fbq is not defined/i,
        /Load failed/i,
        /Fetch is aborted/i,
        /Failed to fetch/i,
        /NetworkError when attempting to fetch resource./i
    ];

    readonly #noisyUrls: RegExp[] = [/pinterest\.com/, /js-agent\.newrelic\.com/];

    readonly #noisyRequestUrls: RegExp[] = [/pinterest\.com/, /measurement-api.criteo\.com/];

    /**
     * Checks if any stack trace frame comes from known noisy third-party script URLs.
     * Focuses on the script origin, not the network destination.
     */
    #isNoisyUrl(stackFrames?: Array<{ filename?: string }>): boolean {
        return (
            stackFrames?.some((frame) =>
                this.#noisyUrls.some((pattern) => pattern.test(frame?.filename || ''))
            ) || false
        );
    }

    /**
     * Checks if the request URL involved in the error points to a noisy third-party domain.
     * Focuses on the HTTP destination, not the script file origin.
     */
    #isNoisyRequestUrl(url?: string): boolean {
        if (!url) return false;
        return this.#noisyRequestUrls.some((pattern) => pattern.test(url));
    }

    #isNoisyMessage(message: string): boolean {
        return this.#noisyMessages.some((regex) => regex.test(message));
    }

    #hasOurDomainInStack(stackFrames?: Array<{ filename?: string }>): boolean | undefined {
        return stackFrames?.some((frame): boolean =>
            [
                'natuurhuisje.nl',
                'natuurhuisje.be',
                'nature.house',
                'naturhaeuschen.de',
                'casanellanatura.it',
                'maisonnature.fr',
                'maisonnature.be'
            ].some((domain) => frame?.filename?.includes(domain))
        );
    }

    public shouldIgnore(event: ErrorEvent): boolean {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        const requestUrl = event.request?.url;
        const errorMessage = event.exception?.values?.[0]?.value || '';

        const isNoisyByUrl = this.#isNoisyUrl(frames);
        const isNoisyByMessage = this.#isNoisyMessage(errorMessage);
        const isNoisyByRequestUrl = this.#isNoisyRequestUrl(requestUrl);

        return (
            (isNoisyByUrl || isNoisyByMessage || isNoisyByRequestUrl) &&
            !this.#hasOurDomainInStack(frames)
        );
    }
}
